/**
 * Barras do gráfico do popup — funções puras (testadas em `barras.test.ts`).
 *
 * O gráfico é sempre o período inteiro que o CEMADEN publica: 24 h.
 *
 * Não há janela a escolher. Oferecer "acumular em 1 h" sobre um dado de 24 h
 * era uma escolha sem lastro na fonte, e ainda fazia o desenho se mexer junto
 * com a animação — duas coisas mudando de uma vez, impossível ler qualquer uma.
 */
import { chuvaNaFatia } from './series';
import type { Grade, SerieHoraria } from './types';

export interface Barra {
  /** Texto do tooltip. */
  rotulo: string;
  /** mm na barra, ou null quando não houve medição. */
  mm: number | null;
  /** Instante do FIM da fatia (epoch ms UTC). */
  ms: number;
  /** Barra correspondente à fatia do quadro exibido. */
  atual?: boolean;
}

/** Rótulo de hora:minuto em UTC, no fuso do dado. */
function hhmm(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

/**
 * Barras a partir da série horária do CEMADEN, para a janela de 24 h.
 *
 * `acumulados` é uma matriz `[dia][k]` em que `k` percorre `horarios` e só um
 * dia costuma ter valor em cada `k`. Percorrer dia a dia parecia equivalente e
 * não é: quando mais de um dia traz valor no mesmo `k`, cada hora vira duas
 * barras e o gráfico de 24 h aparece com 48. Por isso a varredura é por HORA,
 * e o primeiro dia com valor naquele `k` ganha.
 */
export function barrasDaSerie(serie: SerieHoraria): Barra[] {
  const barras: Barra[] = [];
  for (let k = 0; k < serie.horas.length; k++) {
    const dia = serie.acumulados.findIndex((l) => l[k] !== null && l[k] !== undefined);
    // Hora sem medição vira barra NULA, e não desaparece: pular a hora fazia
    // as restantes se esticarem pela largura toda, então um gráfico com 3
    // leituras parecia 24 h contínuas de dado.
    barras.push({
      rotulo: `${serie.horas[k] ?? ''} · ${serie.datas[Math.max(0, dia)] ?? ''}`,
      ms: 0,
      mm: dia < 0 ? null : serie.acumulados[dia]![k]!,
    });
  }
  return barras;
}


/**
 * Barras do PERÍODO INTEIRO da grade — o histórico fixo do popup.
 *
 * Não depende da janela escolhida nem do quadro exibido. O gráfico andava
 * junto com a animação E com o seletor, e duas coisas se mexendo ao mesmo
 * tempo tornavam impossível ler qualquer uma. Aqui o desenho fica parado, e o
 * que se move é só a marca do instante — uma linha sobre um gráfico estático.
 *
 * Agrega fatias quando 144 barras não caberiam: num gráfico de ~150 px,
 * barras de meio pixel viram ruído.
 */
export function barras24h(
  grade: Grade,
  linha: readonly number[] | undefined,
  slot: number,
  maxBarras = 48,
): Barra[] {
  if (!linha) return [];
  const passo = Math.max(1, Math.ceil(grade.slots / maxBarras));
  const out: Barra[] = [];

  for (let inicio = 0; inicio < grade.slots; inicio += passo) {
    const fim = Math.min(grade.slots - 1, inicio + passo - 1);
    let soma = 0;
    let medidas = 0;
    for (let s = inicio; s <= fim; s++) {
      const v = chuvaNaFatia(linha, s, grade.grandeza);
      if (v === null) continue;
      soma += v;
      medidas++;
    }
    const ms = grade.t0 + fim * grade.passoMin * 60_000;
    out.push({
      rotulo: `${hhmm(ms)} UTC`,
      ms,
      // Nenhuma fatia do intervalo mediu: ausência, não zero.
      mm: medidas === 0 ? null : soma,
      atual: slot >= inicio && slot <= fim,
    });
  }
  return out;
}
